import { Component, HostBinding, inject, OnDestroy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { combineLatest, map, Observable, switchMap, timer } from 'rxjs';
import { MomentTzPipe } from '../../../core/pipe/moment.pipe';
import { RestClientFactory } from '../../../core/market/rest-client-factory';
import { AccountService } from '../../../core/service/account.service';
import dayjs from '../../../core/dayjs-config';
import { toSignal } from '@angular/core/rxjs-interop';
import { KeysService } from '../../../terminal/keys/keys.service';
import { BybitTimeService, BinanceUsdmTimeService, OkxTimeService } from 'src/app/core/market/market-time.service';
import { AppTranslateService } from 'src/app/core/service/translate.service';

@Component({
  selector: 'app-clock',
  imports: [MomentTzPipe, AsyncPipe],
  templateUrl: './clock.component.html',
  styleUrl: './clock.component.css'
})
export class ClockComponent implements OnDestroy {

  private rcf = inject(RestClientFactory)
  private account = inject(AccountService)
  private ks = inject(KeysService)
  private bybitTime = inject(BybitTimeService)
  private binanceUsdmTime = inject(BinanceUsdmTimeService)
  private okxTime = inject(OkxTimeService)
  private translate = inject(AppTranslateService)

  private timeDelta = 0

  private sb = this.ks.activeKey$.pipe(
    switchMap(key => {
      const rest = this.rcf.createForMarket(key.market)
      if (rest.isBybit) return this.bybitTime.offset$
      if (rest.isBinanceUsdm) return this.binanceUsdmTime.offset$
      if (rest.isOkx) return this.okxTime.offset$
      throw new Error(`Unknown market ${key.market}`)
    })
  ).subscribe(offset => this.timeDelta = offset)

  ngOnDestroy() {
    this.sb.unsubscribe()
  }

  protected timezone$ = this.account.getProfile().pipe(
    map(profile => profile ? profile.timezone : dayjs.tz.guess())
  )

  protected clock$ = timer(0, 1000).pipe(
    map(() => Date.now() + this.timeDelta)
  )

  private titleSignal = toSignal(combineLatest([this.timezone$, this.translate.languageChange$] as Observable<string>[]).pipe(
    map((args) => {
      const offset = dayjs.tz(undefined, args[0]).utcOffset()
      const tzName = `UTC${offset >= 0 ? '+' : '-'}${String(Math.abs(offset / 60)).padStart(2, '0')}:${String(Math.abs(offset % 60)).padStart(2, '0')} ${args[0]}`
      return `${this.translate.get('common.screenTime')}: ${tzName} (${this.translate.get('common.timezone')})\nUTC+00:00 (${this.translate.get('common.exchangeTime')})`
    })
  ))

  @HostBinding('attr.title')
  protected get titleAttr() {
    return this.titleSignal()
  }

}
